import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BrowserJiraService } from '@/services/jira-service';
import { withJiraExpiryCheck, type JiraCredentials } from '@/services/jira-token-expiry';
import { atlassianOAuth2 } from '@/services/atlassian-oauth2';
import { useJiraTokenExpiryModal } from '@/components/JiraTokenExpiryModal';
import type { JiraIssue, CorsProxyConfig } from '@/services/types';
import * as audit from '@/services/audit';
import { JiraIssueDetail } from './JiraIssueDetail';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faCheck,
  faChevronDown,
  faChevronRight,
  faCircleExclamation,
  faFilter,
  faFloppyDisk,
  faLayerGroup,
  faLocationDot,
  faMagnifyingGlass,
  faPlus,
  faRotate,
  faSpinner,
  faTrash,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

// ==================== Filter Combobox ====================

function FilterCombobox({ value, onChange, options, placeholder, isDark }: {
  value: string[]; onChange: (v: string[]) => void; options: string[]; placeholder: string; isDark: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = search ? options.filter(o => o.toLowerCase().includes(search.toLowerCase())) : options;
  const active = value.length > 0;
  const label = active ? (value.length === 1 ? value[0] : `${value.length} selected`) : placeholder;

  const toggle = (opt: string) => {
    onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <button className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border transition-colors min-w-[120px] max-w-[200px] ${active ? 'border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-300' : isDark ? 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`}>
          <span className="truncate flex-1 text-left">{label}</span>
          {active && <span className="flex-shrink-0 px-1 py-0.5 rounded bg-blue-500 text-white text-[9px] font-bold leading-none">{value.length}</span>}
          <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3 flex-shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="!w-64 !p-0 overflow-hidden">
        <div className={`p-2 border-b ${isDark ? 'border-slate-700/50' : 'border-gray-100'}`}>
          <div className="relative">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${placeholder.toLowerCase().replace('all ', '')}...`}
              className={`w-full pl-7 pr-2 py-1.5 text-xs rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${isDark ? 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'}`}
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-[240px] overflow-y-auto overflow-x-hidden py-1">
          {active && (
            <button
              onClick={() => { onChange([]); setSearch(''); }}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${isDark ? 'text-red-400 hover:bg-slate-800' : 'text-red-500 hover:bg-gray-50'}`}
            >
              <FontAwesomeIcon icon={faXmark} className="h-3 w-3 flex-shrink-0" />
              <span>Clear selection</span>
            </button>
          )}
          {filtered.map(opt => {
            const selected = value.includes(opt);
            return (
              <button
                key={opt}
                onClick={() => toggle(opt)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${selected ? isDark ? 'bg-blue-500/10 text-blue-300' : 'bg-blue-50 text-blue-600' : isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                <div className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${selected ? 'bg-blue-500 border-blue-500' : isDark ? 'border-slate-600' : 'border-gray-300'}`}>
                  {selected && <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5 text-white" />}
                </div>
                <span className="break-all min-w-0">{opt}</span>
              </button>
            );
          })}
          {filtered.length === 0 && <div className="px-3 py-4 text-xs text-center text-gray-400">No matches</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ==================== JQL → Human Readable ====================

function jqlToHuman(jql: string): string {
  if (!jql.trim()) return 'No conditions';
  let text = jql.trim();

  // 1. Strip ORDER BY clause entirely
  text = text.replace(/\bORDER\s+BY\b.*/gi, '').trim();

  // 2. Strip trailing/leading AND/OR left over after stripping
  text = text.replace(/\b(AND|OR)\s*$/gi, '').trim();

  // 3. Clean custom field bracket annotations: "fieldName[Dropdown]" → "fieldName"
  text = text.replace(/\[[^\]]*\]/g, '');

  // 4. Humanise functions
  text = text.replace(/\bcurrentUser\(\)/gi, 'me');
  text = text.replace(/\bstartOfDay\(\)/gi, 'start of today');
  text = text.replace(/\bendOfDay\(\)/gi, 'end of today');
  text = text.replace(/\bstartOfWeek\(\)/gi, 'start of this week');
  text = text.replace(/\bstartOfMonth\(\)/gi, 'start of this month');

  // 5. Flatten parenthesised comma lists: ("a", "b") → a, b
  text = text.replace(/\(([^()]+)\)/g, (_, inner: string) => {
    return inner.replace(/["']/g, '').trim();
  });

  // 6. Remove remaining quotes
  text = text.replace(/["']/g, '');

  // 7. Replace operators (order matters — multi-char first)
  text = text.replace(/\bNOT\s+IN\b/gi, 'not one of');
  text = text.replace(/\bIN\b/gi, 'is one of');
  text = text.replace(/\bIS\s+NOT\s+EMPTY\b/gi, 'has a value');
  text = text.replace(/\bIS\s+EMPTY\b/gi, 'is empty');
  text = text.replace(/\bIS\s+NOT\b/gi, 'is not');
  text = text.replace(/!=/g, ' is not ');
  text = text.replace(/>=/g, ' is on or after ');
  text = text.replace(/<=/g, ' is on or before ');
  text = text.replace(/(?<!!|>|<)=(?!=)/g, ' is ');
  text = text.replace(/(?<!<)>/g, ' is after ');
  text = text.replace(/(?<!>)</g, ' is before ');
  text = text.replace(/!~/g, ' does not contain ');
  text = text.replace(/(?<!!)~/g, ' contains ');
  text = text.replace(/\bWAS\s+NOT\b/gi, 'was not');
  text = text.replace(/\bWAS\b/gi, 'was');
  text = text.replace(/\bCHANGED\b/gi, 'changed');

  // 8. Logical operators → lowercase
  text = text.replace(/\bAND\b/gi, 'and');
  text = text.replace(/\bOR\b/gi, 'or');

  // 9. Known field name aliases (case-insensitive, word-boundary)
  const fields: [RegExp, string][] = [
    [/\bissuetype\b/gi, 'Type'], [/\bstatusCategory\b/gi, 'Status Category'],
    [/\bfixVersion\b/gi, 'Fix Version'], [/\baffectedVersion\b/gi, 'Affected Version'],
    [/\bduedate\b/gi, 'Due Date'], [/\bworklogDate\b/gi, 'Work Log Date'],
    [/\bDeploymentEnvironmentType\b/gi, 'Deployment Environment'],
  ];
  for (const [re, friendly] of fields) {
    text = text.replace(re, friendly);
  }

  // 10. Humanise relative dates: -30d → 30 days ago, -24h → 24 hours ago
  text = text.replace(/-(\d+)d\b/g, '$1 days ago');
  text = text.replace(/-(\d+)h\b/g, '$1 hours ago');
  text = text.replace(/-(\d+)w\b/g, '$1 weeks ago');
  text = text.replace(/-(\d+)m\b/g, '$1 months ago');

  // 11. Collapse whitespace and capitalise
  text = text.replace(/\s+/g, ' ').trim();
  if (!text) return 'No conditions';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// ==================== Quick Filter Combobox ====================

function QuickFilterCombobox({ filters, activeIds, onToggle, onClear, onDelete, onAdd, isDark }: {
  filters: QuickFilter[];
  activeIds: Set<number | string>;
  onToggle: (id: number | string) => void;
  onClear: () => void;
  onDelete: (id: number | string) => void;
  onAdd: (name: string, jql: string) => void;
  isDark: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newJql, setNewJql] = useState('');
  const filtered = search ? filters.filter(f => f.name.toLowerCase().includes(search.toLowerCase()) || f.jql.toLowerCase().includes(search.toLowerCase())) : filters;
  const activeCount = activeIds.size;
  const label = activeCount > 0 ? (activeCount === 1 ? filters.find(f => activeIds.has(f.id))?.name || '1 selected' : `${activeCount} selected`) : 'Quick Filters';

  const handleAdd = () => {
    if (!newName.trim() || !newJql.trim()) return;
    onAdd(newName.trim(), newJql.trim());
    setNewName(''); setNewJql(''); setAdding(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setSearch(''); setAdding(false); setNewName(''); setNewJql(''); } }}>
      <PopoverTrigger asChild>
        <button className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border transition-colors min-w-[120px] max-w-[200px] ${activeCount > 0 ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300' : isDark ? 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`}>
          <FontAwesomeIcon icon={faBolt} className="h-3 w-3 flex-shrink-0 text-amber-500" />
          <span className="truncate flex-1 text-left">{label}</span>
          {activeCount > 0 && <span className="flex-shrink-0 px-1 py-0.5 rounded bg-amber-500 text-white text-[9px] font-bold leading-none">{activeCount}</span>}
          <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3 flex-shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="!w-80 !p-0 overflow-hidden">
        <div className={`p-2 border-b ${isDark ? 'border-slate-700/50' : 'border-gray-100'}`}>
          <div className="relative">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search quick filters..."
              className={`w-full pl-7 pr-2 py-1.5 text-xs rounded-md border focus:outline-none focus:ring-2 focus:ring-amber-500/30 ${isDark ? 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'}`}
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-[280px] overflow-y-auto py-1">
          {activeCount > 0 && (
            <button
              onClick={() => { onClear(); setSearch(''); }}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${isDark ? 'text-red-400 hover:bg-slate-800' : 'text-red-500 hover:bg-gray-50'}`}
            >
              <FontAwesomeIcon icon={faXmark} className="h-3 w-3 flex-shrink-0" />
              <span>Clear all quick filters</span>
            </button>
          )}
          {filtered.length === 0 && !adding && <div className="px-3 py-4 text-xs text-center text-gray-400">No quick filters found</div>}
          {filtered.map(qf => {
            const selected = activeIds.has(qf.id);
            return (
              <div key={qf.id} className={`flex items-center group ${selected ? isDark ? 'bg-amber-500/10' : 'bg-amber-50' : ''}`}>
                <button
                  onClick={() => onToggle(qf.id)}
                  className={`flex-1 flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${selected ? 'text-amber-700 dark:text-amber-300' : isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <div className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${selected ? 'bg-amber-500 border-amber-500' : isDark ? 'border-slate-600' : 'border-gray-300'}`}>
                    {selected && <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      {qf.source === 'jira' ? <FontAwesomeIcon icon={faBolt} className="h-2.5 w-2.5 text-amber-500 opacity-50 flex-shrink-0" /> : <FontAwesomeIcon icon={faFloppyDisk} className="h-2.5 w-2.5 text-blue-400 opacity-50 flex-shrink-0" />}
                      <span className="break-words">{qf.name}</span>
                    </div>
                    <div className={`text-[10px] break-words leading-snug mt-0.5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{jqlToHuman(qf.jql)}</div>
                  </div>
                </button>
                {qf.source === 'custom' && (
                  <button
                    onClick={() => onDelete(qf.id)}
                    className="mr-2 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                    title="Delete custom filter"
                  >
                    <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {/* Add custom filter */}
        <div className={`border-t p-2 ${isDark ? 'border-slate-700/50' : 'border-gray-100'}`}>
          {adding ? (
            <div className="space-y-1.5">
              <input
                type="text" value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="Filter name"
                className={`w-full px-2 py-1.5 text-xs rounded-md border focus:outline-none focus:ring-1 focus:ring-amber-500/30 ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                autoFocus
              />
              <input
                type="text" value={newJql} onChange={e => setNewJql(e.target.value)}
                placeholder="JQL (e.g. label = '2025-Priority')"
                className={`w-full px-2 py-1.5 text-xs rounded-md border focus:outline-none focus:ring-1 focus:ring-amber-500/30 ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
              />
              <div className="flex items-center gap-1.5">
                <button onClick={handleAdd} disabled={!newName.trim() || !newJql.trim()} className="flex-1 px-2 py-1.5 text-xs font-medium rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed">Save Filter</button>
                <button onClick={() => { setAdding(false); setNewName(''); setNewJql(''); }} className={`px-2 py-1.5 text-xs rounded-md ${isDark ? 'text-slate-400 hover:bg-slate-800' : 'text-gray-500 hover:bg-gray-100'}`}>Cancel</button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className={`w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${isDark ? 'text-slate-400 hover:text-slate-300 hover:bg-slate-800' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
            >
              <FontAwesomeIcon icon={faPlus} className="h-3 w-3" /> Add Custom Filter
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ==================== Quick Filters ====================

interface QuickFilter {
  id: number | string;
  name: string;
  jql: string;
  description?: string;
  source: 'jira' | 'custom';
}

const QF_STORAGE_KEY = 'verigit-timeline-quick-filters';

function loadCustomFilters(projectKey: string): QuickFilter[] {
  try {
    const raw = localStorage.getItem(`${QF_STORAGE_KEY}-${projectKey}`);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveCustomFilters(projectKey: string, filters: QuickFilter[]) {
  localStorage.setItem(`${QF_STORAGE_KEY}-${projectKey}`, JSON.stringify(filters));
}

// ==================== Types ====================

interface TimelineProps {
  inline?: boolean;
  onClose: () => void;
  jiraDomain: string;
  jiraEmail: string;
  jiraApiToken: string;
  corsProxy?: CorsProxyConfig;
  defaultProject?: string;
  defaultBoardId?: number;
  showRainbow?: boolean;
}

interface ProcessedIssue {
  key: string;
  summary: string;
  statusName: string;
  statusCategory: string;
  issueTypeName: string;
  issueTypeIcon?: string;
  isEpic: boolean;
  assigneeName?: string;
  assigneeAvatar?: string;
  labels: string[];
  components: string[];
  startDate: Date;
  endDate: Date;
  dueDate: Date | null;
  hasExplicitStartDate: boolean;
  parentKey: string | null;
  children: ProcessedIssue[];
  childDoneCount: number;
  childTotal: number;
}

type ZoomLevel = 'weeks' | 'months' | 'quarters';

const DAY_MS = 86400000;
const ZOOM_CFG: Record<ZoomLevel, { dayWidth: number; label: string }> = {
  weeks: { dayWidth: 40, label: 'Weeks' },
  months: { dayWidth: 10, label: 'Months' },
  quarters: { dayWidth: 5, label: 'Quarters' },
};

const STATUS_COLORS: Record<string, { bar: string; badge: string }> = {
  done: { bar: 'bg-emerald-600 dark:bg-emerald-400', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300' },
  indeterminate: { bar: 'bg-blue-600 dark:bg-blue-400', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300' },
  new: { bar: 'bg-slate-500 dark:bg-slate-500', badge: 'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-400' },
};

const EPIC_COLORS = [
  { solid: '#3b82f6', gradient: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)' },   // blue→purple→pink
  { solid: '#10b981', gradient: 'linear-gradient(90deg, #10b981, #06b6d4, #3b82f6)' },   // emerald→cyan→blue
  { solid: '#8b5cf6', gradient: 'linear-gradient(90deg, #8b5cf6, #ec4899, #f97316)' },   // purple→pink→orange
  { solid: '#f97316', gradient: 'linear-gradient(90deg, #f97316, #eab308, #10b981)' },   // orange→yellow→emerald
  { solid: '#ec4899', gradient: 'linear-gradient(90deg, #ec4899, #8b5cf6, #3b82f6)' },   // pink→purple→blue
  { solid: '#06b6d4', gradient: 'linear-gradient(90deg, #06b6d4, #3b82f6, #8b5cf6)' },   // cyan→blue→purple
  { solid: '#eab308', gradient: 'linear-gradient(90deg, #eab308, #f97316, #ec4899)' },   // yellow→orange→pink
  { solid: '#ef4444', gradient: 'linear-gradient(90deg, #ef4444, #f97316, #eab308)' },   // red→orange→yellow
  { solid: '#14b8a6', gradient: 'linear-gradient(90deg, #14b8a6, #10b981, #06b6d4)' },   // teal→emerald→cyan
  { solid: '#a855f7', gradient: 'linear-gradient(90deg, #a855f7, #ec4899, #ef4444)' },   // violet→pink→red
];

// ==================== Data Processing ====================

function processIssues(raw: JiraIssue[], startDateFieldId?: string): ProcessedIssue[] {
  const now = new Date();
  return raw.map(issue => {
    const f = issue.fields;
    const created = new Date(f.created);
    const updated = new Date(f.updated);
    // Use Jira's actual start date field if available, otherwise try common custom fields, then fall back to created
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fa = f as any;
    const rawStart = (startDateFieldId && fa[startDateFieldId]) || fa.customfield_10015 || fa.customfield_10016 || null;
    const hasExplicitStartDate = !!rawStart;
    const dueDate = f.duedate ? new Date(f.duedate) : null;
    const resolved = f.resolutiondate ? new Date(f.resolutiondate) : null;
    const cat = f.status.statusCategory.key;

    // Determine start & end dates using full lifecycle when explicit start date is missing
    let startDate: Date;
    let endDate: Date;

    if (rawStart) {
      startDate = new Date(rawStart);
    } else {
      // No explicit start date: use created as the start for full lifecycle bars
      startDate = created;
    }

    if (cat === 'done' && resolved) {
      // Done: bar spans created → resolved (full lifecycle)
      endDate = resolved;
    } else if (dueDate) {
      endDate = dueDate;
    } else if (cat === 'indeterminate') {
      // In progress: bar spans created → now (capped at updated + 3 days)
      endDate = new Date(Math.min(updated.getTime() + 3 * DAY_MS, now.getTime()));
      if (endDate.getTime() - startDate.getTime() < 3 * DAY_MS) endDate = new Date(startDate.getTime() + 3 * DAY_MS);
    } else {
      // To-do: show a small placeholder bar
      endDate = new Date(startDate.getTime() + 7 * DAY_MS);
    }
    if (endDate <= startDate) endDate = new Date(startDate.getTime() + DAY_MS);
    return {
      key: issue.key, summary: f.summary, statusName: f.status.name, statusCategory: cat,
      issueTypeName: f.issuetype.name, issueTypeIcon: f.issuetype.iconUrl || undefined,
      isEpic: f.issuetype.name.toLowerCase() === 'epic',
      assigneeName: f.assignee?.displayName, assigneeAvatar: f.assignee?.avatarUrls?.['24x24'],
      labels: f.labels || [], components: (f.components || []).map(c => c.name),
      startDate, endDate, dueDate, hasExplicitStartDate, parentKey: f.parent?.key || null,
      children: [], childDoneCount: 0, childTotal: 0,
    };
  });
}

function groupByEpic(issues: ProcessedIssue[]): ProcessedIssue[] {
  const epicMap = new Map<string, ProcessedIssue>();
  const childMap = new Map<string, ProcessedIssue[]>();
  const orphans: ProcessedIssue[] = [];
  for (const i of issues) { if (i.isEpic) epicMap.set(i.key, { ...i, children: [] }); }
  for (const i of issues) {
    if (i.isEpic) continue;
    if (i.parentKey && epicMap.has(i.parentKey)) {
      if (!childMap.has(i.parentKey)) childMap.set(i.parentKey, []);
      childMap.get(i.parentKey)!.push(i);
    } else { orphans.push(i); }
  }
  const result: ProcessedIssue[] = [];
  for (const [k, epic] of epicMap) {
    const ch = childMap.get(k) || [];
    let minS = epic.startDate, maxE = epic.endDate;
    if (epic.statusCategory !== 'new' && ch.length > 0) {
      for (const c of ch) {
        // Only let children with explicit start dates influence epic's start; any child can extend end
        if (c.hasExplicitStartDate && c.startDate < minS) minS = c.startDate;
        if (c.endDate > maxE) maxE = c.endDate;
      }
    }
    result.push({ ...epic, startDate: minS, endDate: maxE, children: ch, childDoneCount: ch.filter(c => c.statusCategory === 'done').length, childTotal: ch.length });
  }
  result.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  if (orphans.length > 0) {
    const minS = orphans.reduce((m, o) => o.startDate < m ? o.startDate : m, orphans[0].startDate);
    const maxE = orphans.reduce((m, o) => o.endDate > m ? o.endDate : m, orphans[0].endDate);
    result.push({ key: '__no_epic__', summary: 'No Epic', statusName: '', statusCategory: 'new', issueTypeName: 'Group', isEpic: true, labels: [], components: [], startDate: minS, endDate: maxE, dueDate: null, hasExplicitStartDate: false, parentKey: null, children: orphans, childDoneCount: orphans.filter(o => o.statusCategory === 'done').length, childTotal: orphans.length });
  }
  return result;
}

// ==================== Component ====================

export function TimelineView({ inline, onClose: _onClose, jiraDomain, jiraEmail, jiraApiToken, corsProxy: _corsProxy, defaultProject, defaultBoardId, showRainbow = false }: TimelineProps) {
  // Token expiry modal
  const { Modal: TokenExpiryModal } = useJiraTokenExpiryModal();

  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<{ id: string; key: string; name: string }[]>([]);
  const [internalSelectedProject, setInternalSelectedProject] = useState(defaultProject || '');
  const [rawIssues, setRawIssues] = useState<JiraIssue[]>([]);
  const startDateFieldRef = useRef<string | undefined>(undefined);
  const [zoom, setZoom] = useState<ZoomLevel>('months');
  const [expandedEpics, setExpandedEpics] = useState<Set<string>>(new Set());
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<string[]>([]);
  const [filterLabel, setFilterLabel] = useState<string[]>([]);
  const [filterAssignee, setFilterAssignee] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  // Quick filters state
  const [boardId, setBoardId] = useState<number | undefined>(defaultBoardId);
  const [jiraQuickFilters, setJiraQuickFilters] = useState<QuickFilter[]>([]);
  const [customQuickFilters, setCustomQuickFilters] = useState<QuickFilter[]>([]);
  const [activeQuickFilterIds, setActiveQuickFilterIds] = useState<Set<number | string>>(new Set());

  const [jiraOAuth, setJiraOAuth] = useState<{ oauthToken?: string; cloudId?: string }>({});

  useEffect(() => {
    Promise.all([atlassianOAuth2.getAccessToken(), atlassianOAuth2.getState()])
      .then(([token, state]) => {
        if (token) setJiraOAuth({ oauthToken: token, cloudId: state.token?.cloudId });
      })
      .catch(() => {});
  }, []);

  const service = useMemo(() => {
    if (!jiraDomain || !jiraEmail || !jiraApiToken) return null;
    return new BrowserJiraService({ domain: jiraDomain, email: jiraEmail, apiToken: jiraApiToken, ...jiraOAuth });
  }, [jiraDomain, jiraEmail, jiraApiToken, jiraOAuth]);

  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!service) return;
    
    const loadProjects = async () => {
      try {
        const settings: JiraCredentials = { domain: jiraDomain, email: jiraEmail, apiToken: jiraApiToken, ...jiraOAuth };
        const result = await withJiraExpiryCheck(settings, () => service!.getProjects());
        
        if (result.error) {
          console.error('Failed to load projects:', result.error);
          return;
        }
        
        const projects = result.result;
        if (projects) {
          setProjects(projects);
          if (!internalSelectedProject && projects.length > 0) {
            setInternalSelectedProject(defaultProject || projects[0].key);
          }
        }
      } catch (err) {
        console.error('Failed to load projects:', err);
      }
    };
    
    loadProjects();
  }, [service, defaultProject]);

  // Discover board & fetch quick filters when project changes
  useEffect(() => {
    if (!service || !internalSelectedProject) return;
    setCustomQuickFilters(loadCustomFilters(internalSelectedProject));
    setActiveQuickFilterIds(new Set());

    const fetchQuickFilters = async (bid: number) => {
      const qfs = await service.getQuickFilters(bid);
      setJiraQuickFilters(qfs.sort((a, b) => a.position - b.position).map(qf => ({ ...qf, source: 'jira' as const })));
    };

    if (boardId) {
      fetchQuickFilters(boardId);
    } else {
      service.getBoardForProject(internalSelectedProject).then(board => {
        if (board) { setBoardId(board.id); fetchQuickFilters(board.id); }
      }).catch(() => {});
    }
  }, [service, internalSelectedProject, boardId]);

  const allQuickFilters = useMemo(() => [...jiraQuickFilters, ...customQuickFilters], [jiraQuickFilters, customQuickFilters]);

  const toggleQuickFilter = useCallback((id: number | string) => {
    setActiveQuickFilterIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const addCustomFilter = useCallback((name: string, jql: string) => {
    if (!name || !jql || !internalSelectedProject) return;
    const updated = customQuickFilters.some(f => f.jql === jql)
      ? customQuickFilters.map(f => f.jql === jql ? { ...f, name } : f)
      : [...customQuickFilters, { id: Date.now(), name, jql, source: 'custom' as const }];
    setCustomQuickFilters(updated);
    saveCustomFilters(internalSelectedProject, updated);
  }, [customQuickFilters, internalSelectedProject]);

  const deleteCustomFilter = useCallback((id: number | string) => {
    if (!internalSelectedProject) return;
    const updated = customQuickFilters.filter(f => f.id !== id);
    setCustomQuickFilters(updated);
    saveCustomFilters(internalSelectedProject, updated);
  }, [customQuickFilters, internalSelectedProject]);

  // Build active JQL fragments from quick filters
  const activeQfJql = useMemo(() => {
    if (activeQuickFilterIds.size === 0) return '';
    return allQuickFilters
      .filter(qf => activeQuickFilterIds.has(qf.id))
      .map(qf => {
        // Strip ORDER BY from filter fragments — the main query adds its own
        const cleaned = qf.jql.replace(/\bORDER\s+BY\b.*/gi, '').trim();
        return `(${cleaned})`;
      })
      .filter(s => s !== '()')
      .join(' AND ');
  }, [activeQuickFilterIds, allQuickFilters]);

  const loadTimeline = useCallback(async () => {
    if (!service || !internalSelectedProject) return;
    setLoading(true);
    setError(null);
    try {
      audit.auditAnalysisStarted('jira', { view: 'timeline', project: internalSelectedProject });
      const t0 = Date.now();
      const settings: JiraCredentials = { domain: jiraDomain, email: jiraEmail, apiToken: jiraApiToken, ...jiraOAuth };
      
      // Discover the Start date field if not already known
      let sdField = startDateFieldRef.current;
      if (!sdField) {
        try {
          const fieldsResult = await withJiraExpiryCheck(settings, () => service!.getFields());
          if (fieldsResult.error) {
            throw new Error(fieldsResult.error.message);
          }
          const fields = fieldsResult.result;
          if (fields) {
            const found = fields.find(f => /^start\s*date$/i.test(f.name)) || fields.find(f => /target\s*start/i.test(f.name));
            if (found) {
              sdField = found.id;
              startDateFieldRef.current = found.id;
              console.log(`[Timeline] Discovered start date field: ${found.name} (${found.id})`);
            } else {
              console.warn('[Timeline] Could not find start date field. Available date fields:', fields.filter(f => /date|start/i.test(f.name)).map(f => `${f.name} (${f.id})`));
            }
          }
        } catch (e) { console.warn('[Timeline] Field discovery failed:', e); }
      }
      const searchFields = ['summary', 'status', 'issuetype', 'assignee', 'reporter', 'priority', 'created', 'updated', 'duedate', 'resolutiondate', 'components', 'labels', 'sprint', 'parent'];
      if (sdField) searchFields.push(sdField);
      // Always include common start date custom fields as fallback
      if (!searchFields.includes('customfield_10015')) searchFields.push('customfield_10015');
      if (!searchFields.includes('customfield_10016')) searchFields.push('customfield_10016');
      let jql = `project = "${internalSelectedProject}" AND issuetype != Sub-task`;
      if (activeQfJql) jql += ` AND ${activeQfJql}`;
      jql += ' ORDER BY rank ASC';
      
      // Main search with expiry detection
      const searchResult = await withJiraExpiryCheck(settings, () => service!.searchIssues(jql, searchFields));
      if (searchResult.error) {
        throw new Error(searchResult.error.message);
      }
      const issues = searchResult.result;
      if (!issues) {
        throw new Error('No issues returned from search');
      }

      // When quick filters are active, matched epics may have lost their children.
      // Do a second pass to fetch children of any epics found.
      if (activeQfJql) {
        const epicKeys = issues.filter(i => i.fields?.issuetype?.name === 'Epic').map(i => i.key);
        if (epicKeys.length > 0) {
          const seenKeys = new Set(issues.map(i => i.key));
          // Fetch in batches of 30 epic keys to avoid JQL length limits
          for (let i = 0; i < epicKeys.length; i += 30) {
            const batch = epicKeys.slice(i, i + 30);
            const childJql = `parent IN (${batch.join(',')}) AND issuetype != Sub-task ORDER BY rank ASC`;
            const children = await service.searchIssues(childJql, searchFields);
            for (const child of children) {
              if (!seenKeys.has(child.key)) {
                issues.push(child);
                seenKeys.add(child.key);
              }
            }
          }
        }
      }

      setRawIssues(issues);
      setExpandedEpics(new Set());
      audit.auditAnalysisCompleted('jira', Date.now() - t0, { view: 'timeline', project: internalSelectedProject, issueCount: issues.length });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch timeline'); audit.auditAnalysisFailed('jira', err.message || 'Unknown error', { view: 'timeline', project: internalSelectedProject });
    } finally { setLoading(false); }
  }, [service, internalSelectedProject, activeQfJql]);

  useEffect(() => { if (internalSelectedProject) loadTimeline(); }, [internalSelectedProject, loadTimeline]);

  const processed = useMemo(() => processIssues(rawIssues, startDateFieldRef.current), [rawIssues]);
  const grouped = useMemo(() => groupByEpic(processed), [processed]);

  const epicColorMap = useMemo(() => {
    const map = new Map<string, typeof EPIC_COLORS[0]>();
    let idx = 0;
    for (const g of grouped) {
      if (g.key !== '__no_epic__') { map.set(g.key, EPIC_COLORS[idx % EPIC_COLORS.length]); idx++; }
    }
    return map;
  }, [grouped]);

  const filterOpts = useMemo(() => {
    const s = new Set<string>(), t = new Set<string>(), l = new Set<string>(), a = new Set<string>();
    for (const i of processed) { s.add(i.statusName); t.add(i.issueTypeName); i.labels.forEach(x => l.add(x)); if (i.assigneeName) a.add(i.assigneeName); }
    return { statuses: [...s].sort(), types: [...t].sort(), labels: [...l].sort(), assignees: [...a].sort() };
  }, [processed]);

  const filteredGrouped = useMemo(() => {
    const match = (i: ProcessedIssue) => {
      if (filterSearch && !i.key.toLowerCase().includes(filterSearch.toLowerCase()) && !i.summary.toLowerCase().includes(filterSearch.toLowerCase())) return false;
      if (filterStatus.length && !filterStatus.includes(i.statusName)) return false;
      if (filterType.length && !filterType.includes(i.issueTypeName)) return false;
      if (filterLabel.length && !i.labels.some(l => filterLabel.includes(l))) return false;
      if (filterAssignee.length && (!i.assigneeName || !filterAssignee.includes(i.assigneeName))) return false;
      return true;
    };
    return grouped.map(e => { const ch = e.children.filter(match); if (!match(e) && ch.length === 0) return null; return { ...e, children: ch, childTotal: ch.length, childDoneCount: ch.filter(c => c.statusCategory === 'done').length }; }).filter(Boolean) as ProcessedIssue[];
  }, [grouped, filterSearch, filterStatus, filterType, filterLabel, filterAssignee]);

  const { timelineStart, timelineEnd, totalDays } = useMemo(() => {
    const all = filteredGrouped.flatMap(g => [g, ...g.children]);
    if (!all.length) { const n = new Date(); return { timelineStart: new Date(n.getTime() - 30 * DAY_MS), timelineEnd: new Date(n.getTime() + 90 * DAY_MS), totalDays: 120 }; }
    let mn = all[0].startDate, mx = all[0].endDate;
    for (const i of all) { if (i.startDate < mn) mn = i.startDate; if (i.endDate > mx) mx = i.endDate; }
    const padDays = zoom === 'quarters' ? 180 : zoom === 'months' ? 60 : 14;
    const pad = padDays * DAY_MS;
    const s = new Date(mn.getTime() - pad); const e = new Date(mx.getTime() + pad);
    return { timelineStart: s, timelineEnd: e, totalDays: Math.ceil((e.getTime() - s.getTime()) / DAY_MS) };
  }, [filteredGrouped, zoom]);

  const dayWidth = ZOOM_CFG[zoom].dayWidth;
  const chartWidth = totalDays * dayWidth;

  useEffect(() => {
    if (!chartRef.current) return;
    const px = ((Date.now() - timelineStart.getTime()) / DAY_MS) * dayWidth;
    chartRef.current.scrollLeft = Math.max(0, px - chartRef.current.clientWidth * 0.15);
  }, [timelineStart, dayWidth, rawIssues]);

  const handleChartScroll = useCallback(() => {
    if (chartRef.current && listRef.current) listRef.current.scrollTop = chartRef.current.scrollTop;
    if (chartRef.current && headerRef.current) headerRef.current.scrollLeft = chartRef.current.scrollLeft;
    if (chartRef.current) setScrollY(chartRef.current.scrollTop);
  }, []);

  const rows = useMemo(() => {
    const r: { issue: ProcessedIssue; depth: number; isEpic: boolean }[] = [];
    for (const e of filteredGrouped) { r.push({ issue: e, depth: 0, isEpic: true }); if (expandedEpics.has(e.key)) for (const c of e.children) r.push({ issue: c, depth: 1, isEpic: false }); }
    return r;
  }, [filteredGrouped, expandedEpics]);

  const timeMarkers = useMemo(() => {
    const ms: { label: string; x: number; isMajor: boolean }[] = [];
    if (zoom === 'weeks') {
      const d = new Date(timelineStart); d.setDate(d.getDate() + (7 - d.getDay()) % 7);
      while (d <= timelineEnd) { ms.push({ label: d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }), x: ((d.getTime() - timelineStart.getTime()) / DAY_MS) * dayWidth, isMajor: d.getDate() <= 7 }); d.setDate(d.getDate() + 7); }
    } else if (zoom === 'months') {
      const d = new Date(timelineStart); d.setDate(1); if (d < timelineStart) d.setMonth(d.getMonth() + 1);
      while (d <= timelineEnd) { ms.push({ label: d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }), x: ((d.getTime() - timelineStart.getTime()) / DAY_MS) * dayWidth, isMajor: true }); d.setMonth(d.getMonth() + 1); }
    } else {
      const d = new Date(timelineStart); d.setDate(1); if (d < timelineStart) d.setMonth(d.getMonth() + 1);
      while (d <= timelineEnd) {
        const isQ = d.getMonth() % 3 === 0;
        const qNum = Math.floor(d.getMonth() / 3) + 1;
        ms.push({ label: isQ ? `Q${qNum} ${d.getFullYear()}` : d.toLocaleDateString('en-AU', { month: 'short' }), x: ((d.getTime() - timelineStart.getTime()) / DAY_MS) * dayWidth, isMajor: isQ });
        d.setMonth(d.getMonth() + 1);
      }
    }
    return ms;
  }, [timelineStart, timelineEnd, dayWidth, zoom]);

  const todayX = ((Date.now() - timelineStart.getTime()) / DAY_MS) * dayWidth;
  const ROW_H = 36;
  const HDR_H = 48;
  const LIST_W = 380;
  const DETAIL_H = 400;
  const [selectedIssueKey, setSelectedIssueKey] = useState<string | null>(null);
  const [scrollY, setScrollY] = useState(0);
  const selectedRowIdx = useMemo(() => {
    if (!selectedIssueKey) return -1;
    return rows.findIndex(r => r.issue.key === selectedIssueKey);
  }, [rows, selectedIssueKey]);
  const hasFilters = !!(filterSearch || filterStatus.length || filterType.length || filterLabel.length || filterAssignee.length || activeQuickFilterIds.size);
  const sc = (cat: string) => STATUS_COLORS[cat] || STATUS_COLORS.new;

  return (
    <div className={`${inline ? 'h-[calc(100vh-4rem)]' : 'fixed inset-0 z-[100]'} flex flex-col overflow-hidden font-sans`} style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
      {/* Header */}
      <div className={`flex-shrink-0 z-10 bg-gray-50/80 dark:bg-slate-800/30 backdrop-blur-xl border-b ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'}`}>
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faLocationDot} className="h-5 w-5 text-blue-400" />
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Roadmap</h1>
            <span className="text-xs text-gray-400 dark:text-slate-500">Timeline & Epic Tracking</span>
          </div>
          <div className="flex items-center gap-2">
            <Select value={internalSelectedProject} onValueChange={setInternalSelectedProject}>
              <SelectTrigger className="h-8 w-auto min-w-[180px] max-w-[280px] px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => (
                  <SelectItem key={p.key} value={p.key} className="text-xs">
                    {p.key} — {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button onClick={loadTimeline} disabled={loading} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`} title="Refresh">
              <FontAwesomeIcon icon={faRotate} className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${hasFilters ? 'bg-blue-500/20 text-blue-600 dark:text-blue-300 border border-blue-500/30' : isDark ? 'bg-slate-800 text-slate-300 border border-slate-700' : 'bg-white text-gray-600 border border-gray-200'}`}>
              <FontAwesomeIcon icon={faFilter} className="h-3.5 w-3.5" /> Filters {hasFilters && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-blue-500 text-white text-[10px]">!</span>}
            </button>
            <div className={`flex rounded-lg border overflow-hidden ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              {(['weeks', 'months', 'quarters'] as ZoomLevel[]).map(z => (
                <button key={z} onClick={() => setZoom(z)} className={`px-3 py-1.5 text-[11px] font-medium transition-colors ${zoom === z ? 'bg-blue-500 text-white' : isDark ? 'bg-slate-800 text-slate-400 hover:text-white' : 'bg-white text-gray-500 hover:text-gray-700'}`}>{ZOOM_CFG[z].label}</button>
              ))}
            </div>
            <button onClick={() => { if (!chartRef.current) return; chartRef.current.scrollTo({ left: Math.max(0, todayX - chartRef.current.clientWidth * 0.15), behavior: 'smooth' }); }} className={`px-3 py-1.5 text-[11px] font-medium rounded-lg ${isDark ? 'bg-slate-800 text-slate-300 border border-slate-700' : 'bg-white text-gray-600 border border-gray-200'}`}>Today</button>
          </div>
        </div>
        {showFilters && (
          <div className={`px-4 py-3 border-t flex flex-wrap items-center gap-2 ${isDark ? 'border-white/[0.06] bg-slate-900/50' : 'border-black/[0.06] bg-gray-50'}`}>
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input type="text" value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder="Search issues..." className={`w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`} />
            </div>
            {filterOpts.statuses.length > 0 && <FilterCombobox value={filterStatus} onChange={setFilterStatus} options={filterOpts.statuses} placeholder="All Statuses" isDark={isDark} />}
            {filterOpts.types.length > 0 && <FilterCombobox value={filterType} onChange={setFilterType} options={filterOpts.types} placeholder="All Types" isDark={isDark} />}
            {filterOpts.assignees.length > 0 && <FilterCombobox value={filterAssignee} onChange={setFilterAssignee} options={filterOpts.assignees} placeholder="All Assignees" isDark={isDark} />}
            {filterOpts.labels.length > 0 && <FilterCombobox value={filterLabel} onChange={setFilterLabel} options={filterOpts.labels} placeholder="All Labels" isDark={isDark} />}
            <QuickFilterCombobox filters={allQuickFilters} activeIds={activeQuickFilterIds} onToggle={toggleQuickFilter} onClear={() => setActiveQuickFilterIds(new Set())} onDelete={deleteCustomFilter} onAdd={addCustomFilter} isDark={isDark} />
            {hasFilters && <button onClick={() => { setFilterSearch(''); setFilterStatus([]); setFilterType([]); setFilterLabel([]); setFilterAssignee([]); setActiveQuickFilterIds(new Set()); }} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"><FontAwesomeIcon icon={faXmark} className="h-3 w-3" /> Clear</button>}
          </div>
        )}
      </div>

      {loading && <div className="flex-1 flex items-center justify-center"><FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin text-gray-400" /><span className="ml-3 text-sm text-gray-500 dark:text-slate-400">Loading timeline...</span></div>}
      {error && !loading && <div className="flex-1 flex items-center justify-center p-8"><div className="flex items-center gap-3 text-red-500 bg-red-50 dark:bg-red-500/10 px-4 py-3 rounded-xl"><FontAwesomeIcon icon={faCircleExclamation} className="h-5 w-5" /><span className="text-sm">{error}</span></div></div>}

      {!loading && !error && (
        <div className="flex-1 flex overflow-hidden relative">
          {/* Left: issue list */}
          <div className="flex-shrink-0 flex flex-col" style={{ width: LIST_W }}>
            <div className={`flex items-center px-3 font-medium text-[11px] uppercase tracking-wider border-b border-r ${isDark ? 'bg-slate-800/80 text-slate-500 border-slate-700/50' : 'bg-gray-50 text-gray-400 border-gray-200'}`} style={{ height: HDR_H }}><span className="flex-1">Work</span><span className="w-20 text-right">Status</span></div>
            <div ref={listRef} onScroll={() => { if (listRef.current && chartRef.current) { chartRef.current.scrollTop = listRef.current.scrollTop; setScrollY(listRef.current.scrollTop); } }} className={`flex-1 overflow-y-auto overflow-x-hidden border-r ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`} style={{ scrollbarWidth: 'none' }}>
              {rows.length === 0 && <div className="p-8 text-center text-sm text-gray-400 dark:text-slate-500">No issues found.</div>}
              {rows.map(({ issue: i, depth, isEpic: ie }) => {
                const isSelected = !ie && selectedIssueKey === i.key;
                return (
                  <div key={i.key}>
                    <div
                      className={`flex items-center gap-2 px-3 border-b transition-colors ${ie ? isDark ? 'bg-slate-800/60 border-slate-700/30 hover:bg-slate-800/90' : 'bg-gray-50/80 border-gray-100 hover:bg-gray-100/80' : isSelected ? isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200/50' : isDark ? 'border-slate-800/30 hover:bg-slate-800/40 cursor-pointer' : 'border-gray-50 hover:bg-gray-50/50 cursor-pointer'}`}
                      style={{ height: ROW_H, paddingLeft: depth * 20 + 12 }}
                      onClick={() => { if (ie) { setExpandedEpics(prev => { const n = new Set(prev); n.has(i.key) ? n.delete(i.key) : n.add(i.key); return n; }); } else if (i.key !== '__no_epic__') { setSelectedIssueKey(prev => prev === i.key ? null : i.key); } }}
                    >
                      {ie ? <span className="p-0.5 -ml-1">{expandedEpics.has(i.key) ? <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />}</span> : <span className="w-4" />}
                      {i.issueTypeIcon ? <img src={i.issueTypeIcon} alt="" className="h-3.5 w-3.5 flex-shrink-0" /> : <FontAwesomeIcon icon={faLayerGroup} className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
                      <div className="flex-1 min-w-0 flex items-center gap-1.5">
                        <span className={`flex-shrink-0 text-[11px] font-mono ${ie ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-slate-500'}`}>{i.key !== '__no_epic__' ? i.key : ''}</span>
                        <span className={`truncate text-xs ${ie ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-600 dark:text-slate-300'}`}>{i.summary}</span>
                      </div>
                      {ie && i.childTotal > 0 && <span className="flex-shrink-0 text-[10px] text-gray-400 dark:text-slate-500">{i.childDoneCount}/{i.childTotal}</span>}
                      {i.statusName && <span className={`flex-shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${sc(i.statusCategory).badge}`}>{i.statusName}</span>}
                    </div>
                    {isSelected && <div style={{ height: DETAIL_H }} />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Gantt chart */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div ref={headerRef} className={`flex-shrink-0 border-b overflow-hidden ${isDark ? 'bg-slate-800/80 border-slate-700/50' : 'bg-gray-50 border-gray-200'}`} style={{ height: HDR_H }}>
              <div className="relative" style={{ width: chartWidth, height: HDR_H }}>
                {timeMarkers.map((m, i) => <div key={i} className="absolute top-0 h-full flex flex-col justify-end" style={{ left: m.x }}><span className={`text-[10px] font-medium px-2 pb-1 whitespace-nowrap ${m.isMajor ? 'text-gray-600 dark:text-slate-300' : 'text-gray-400 dark:text-slate-500'}`}>{m.label}</span><div className={`w-px h-2 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`} /></div>)}
                <div className="absolute top-0 h-full" style={{ left: todayX }}><div className="w-0.5 h-full bg-red-500/70" /></div>
              </div>
            </div>
            <div ref={chartRef} onScroll={handleChartScroll} className="flex-1 overflow-auto">
              <div className="relative" style={{ width: chartWidth }}>
                {/* Grid lines + today line */}
                {timeMarkers.filter(m => m.isMajor).map((m, i) => <div key={`g${i}`} className={`absolute top-0 bottom-0 w-px ${isDark ? 'bg-slate-800/80' : 'bg-gray-100'}`} style={{ left: m.x }} />)}
                <div className="absolute top-0 bottom-0 w-0.5 bg-red-500/40 z-10" style={{ left: todayX }} />
                {rows.map(({ issue: i, isEpic: ie }, idx) => {
                  const sx = ((i.startDate.getTime() - timelineStart.getTime()) / DAY_MS) * dayWidth;
                  const ex = ((i.endDate.getTime() - timelineStart.getTime()) / DAY_MS) * dayWidth;
                  const w = Math.max(ex - sx, 4);
                  const prog = ie && i.childTotal > 0 ? (i.childDoneCount / i.childTotal) * 100 : i.statusCategory === 'done' ? 100 : 0;
                  const isSelected = !ie && selectedIssueKey === i.key;
                  const epicKey = ie ? i.key : i.parentKey;
                  const epicColor = epicKey ? epicColorMap.get(epicKey) : undefined;
                  const barBg = epicColor
                    ? showRainbow
                      ? { background: epicColor.gradient, backgroundSize: '200% 100%', animation: 'rainbow-slide 3s ease infinite' }
                      : { background: epicColor.solid }
                    : undefined;
                  const fallbackBar = !epicColor ? sc(i.statusCategory).bar : '';
                  // Only show bars for epics or children with explicit dates (matching Jira)
                  const showBar = ie || i.hasExplicitStartDate || !!i.dueDate;
                  return (
                    <div key={i.key}>
                      <div className="relative" style={{ height: ROW_H }}>
                        <div className={`absolute inset-0 ${ie ? isDark ? 'bg-slate-800/30' : 'bg-gray-50/50' : idx % 2 === 0 ? '' : isDark ? 'bg-slate-900/20' : 'bg-gray-50/30'}`} />
                        {showBar && <div className="absolute flex items-center" style={{ left: sx, top: ie ? 8 : 10, height: ie ? 20 : 16 }}>
                          <div className={`relative rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer ${ie ? 'h-5' : 'h-4'}`} style={{ width: w }} title={`${i.key}: ${i.summary}\n${i.startDate.toLocaleDateString()} → ${i.endDate.toLocaleDateString()}\nStatus: ${i.statusName}`} onClick={() => { if (ie) { setExpandedEpics(prev => { const n = new Set(prev); n.has(i.key) ? n.delete(i.key) : n.add(i.key); return n; }); } else if (i.key !== '__no_epic__') { setSelectedIssueKey(prev => prev === i.key ? null : i.key); } }}>
                            {barBg
                              ? <div className="absolute inset-0" style={{ ...barBg, opacity: ie ? 0.35 : 0.9 }} />
                              : <div className={`absolute inset-0 ${fallbackBar} ${ie ? 'opacity-40 dark:opacity-30' : 'opacity-90 dark:opacity-70'}`} />
                            }
                            {ie && prog > 0 && (barBg
                              ? <div className="absolute inset-y-0 left-0" style={{ ...barBg, opacity: 0.9, width: `${prog}%` }} />
                              : <div className={`absolute inset-y-0 left-0 ${fallbackBar} opacity-90 dark:opacity-80`} style={{ width: `${prog}%` }} />
                            )}
                            {w > 100 && <div className="absolute inset-0 flex items-center px-2"><span className="text-[10px] font-medium text-white truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">{i.key} {i.summary}</span></div>}
                            {w > 50 && w <= 100 && <div className="absolute inset-0 flex items-center px-1.5"><span className="text-[10px] font-medium text-white truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">{i.key}</span></div>}
                          </div>
                          {i.dueDate && i.statusCategory !== 'done' && (
                            <div className="ml-1.5 flex-shrink-0"><span className={`text-[9px] ${i.dueDate < new Date() ? 'text-red-500 font-bold' : 'text-gray-400 dark:text-slate-500'}`}>{i.dueDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span></div>
                          )}
                        </div>}
                      </div>
                      {isSelected && <div style={{ height: DETAIL_H }} />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Full-width detail overlay — positioned inline below the clicked row */}
          {selectedIssueKey && selectedRowIdx >= 0 && (
            <div
              className={`absolute left-0 right-0 z-20 overflow-y-auto border-y ${isDark ? 'bg-slate-900/98 border-slate-700/50' : 'bg-white/98 border-gray-200'} backdrop-blur-sm`}
              style={{ top: HDR_H + (selectedRowIdx + 1) * ROW_H - scrollY, height: DETAIL_H }}
            >
              <JiraIssueDetail
                issueKey={selectedIssueKey}
                jiraDomain={jiraDomain}
                jiraEmail={jiraEmail}
                jiraApiToken={jiraApiToken}
                onClose={() => setSelectedIssueKey(null)}
                onNavigate={(key) => { setSelectedIssueKey(null); setTimeout(() => setSelectedIssueKey(key), 50); }}
              />
            </div>
          )}
        </div>
      )}
      
      {/* Token Expiry Modal */}
      <TokenExpiryModal />
    </div>
  );
}
